import { motion } from 'motion/react'
import { useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useCreateQuote } from 'components/hooks/useCreateQuote'
import { useModifyQuote } from 'components/hooks/useModifyQuote'
import { Banner } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import QuoteFlowHeader from '../QuoteFlowHeader'
import { resolveNextStepPath } from '../utils'
import CreateCensus from './CreateCensus'
import { type EmployeeInfo } from './employee_entry/EmployeeEntry'
import EmployeeConfirmation from './EmployeeConfirmation'

export type CreateOrUpdateQuoteProps = {
  prospectId: string
  quoteId?: string
  censusId?: string
  manualEntryEmployees?: EmployeeInfo[]
  nextStepPath: string
  previousStepPath: string
}

export function CreateOrUpdateQuote ({
  prospectId,
  quoteId,
  censusId,
  manualEntryEmployees,
  nextStepPath,
  previousStepPath,
}: CreateOrUpdateQuoteProps) {
  const { assign } = useWindowLocation()

  const [error, setError] = useState<string>()
  const [confirmCensusId, setConfirmCensusId] = useState<string | undefined>()
  const createQuoteMutation = useCreateQuote({ prospectId })
  const updateQuoteMutation = useModifyQuote({ prospectId, quoteId: quoteId || '' })

  const createQuote = async (censusId: string) => {
    if (createQuoteMutation.isIdle && !createQuoteMutation.isSuccess) {
      try {
        const quote = await createQuoteMutation.mutateAsync({
          quote_type: 'quoting_v2',
          quoting_prospect_employee_census_id: censusId,
        })
        navigateToNextStep(quote.id)
      } catch (error) {
        setError((error as GrapeAPIErrorType).message)
      }
    }
  }

  const updateQuote = async (censusId: string) => {
    if (updateQuoteMutation.isIdle && !updateQuoteMutation.isSuccess) {
      try {
        const quote = await updateQuoteMutation.mutateAsync({
          censusId,
        })
        navigateToNextStep(quote.id)
      } catch (error) {
        setError((error as GrapeAPIErrorType).message)
      }
    }
  }

  const navigateToNextStep = (quoteId: string) => {
    const path = resolveNextStepPath(nextStepPath, prospectId, quoteId)
    assign(path)
  }

  const onCensusProcessed = async (censusId: string) => {
    setConfirmCensusId(censusId)
  }

  const onCensusConfirmation = (confirmedCensusId: string) => {
    if (quoteId) {
      updateQuote(confirmedCensusId)
    } else {
      createQuote(confirmedCensusId)
    }
  }

  const renderContent = () => (
    <>
      <div className={confirmCensusId ? 'hidden' : 'block'}>
        <CreateCensus
          prospectId={prospectId}
          censusId={censusId}
          manualEntryEmployees={manualEntryEmployees}
          previousStepPath={previousStepPath}
          onCensusProcessed={onCensusProcessed}
        />
      </div>
      {confirmCensusId && (
        <EmployeeConfirmation
          prospectId={prospectId}
          censusId={confirmCensusId}
          isConfirming={createQuoteMutation.isPending || updateQuoteMutation.isPending}
          onConfirm={() => onCensusConfirmation(confirmCensusId)}
          onBack={() => setConfirmCensusId(undefined)}
        />
      )}
    </>
  )

  return (
    <div className='flex justify-center'>
      <div className='flex flex-col max-w-2xl w-full'>
        <QuoteFlowHeader
          title={confirmCensusId ? 'Confirm employees' : 'Add employees'}
          description={confirmCensusId
            ? 'Review the employees you added and confirm they look correct. You can also select which employees are eligible for health insurance to more accurately predict your costs.'
            : 'Upload or enter employee information'}
        />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {renderContent()}
        </motion.div>
        {error && (
          <Banner
            type='critical'
            title='Error creating quote'
            onDismiss={() => setError(undefined)}
          >
            {error}
          </Banner>
        )}
      </div>
    </div>
  )
}

export default (props: CreateOrUpdateQuoteProps) => {
  return (
    <CreateOrUpdateQuote {...props} />
  )
}
